import React, { useState } from "react";
import { Button, ButtonGroup, Form, InputGroup } from "react-bootstrap";

type Props = {
  searchQuery: string;
  onSearchSubmit: (finalQuery: string) => void;

  sortOptions: string[];
  selectedSort: string;
  onSortChange: (value: string) => void;

  ascending: boolean;
  onToggleDirection: () => void;

  currentPage: number;
  onPageChange: (newPage: number) => void;

  itemsPerPage: number;
  onItemsPerPageChange: (value: number) => void;
  itemsPerPageOptions?: number[];

  totalItems: number;
};

const ListToolbar: React.FC<Props> = ({
  searchQuery,
  onSearchSubmit,
  sortOptions,
  selectedSort,
  onSortChange,
  ascending,
  onToggleDirection,
  currentPage,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  totalItems,
}) => {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSearch = () => onSearchSubmit(localQuery.trim());

  return (
    <div className="d-flex flex-column flex-md-row flex-wrap align-items-center justify-content-between gap-2 mb-0 w-100 small">
      {/* Search Group */}
      <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 200, maxWidth: 320 }}>
        <InputGroup>
          <div style={{ position: "relative", flex: 1 }}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search..."
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") handleSearch();
              }}
              style={{ paddingRight: localQuery ? "2rem" : undefined }}
            />
            {localQuery && (
              <button
                type="button"
                className="btn btn-sm btn-link text-muted"
                onClick={() => {
                  setLocalQuery("");
                  onSearchSubmit("");
                }}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "0.5rem",
                  transform: "translateY(-50%)",
                  textDecoration: "none",
                  fontSize: "1rem",
                  lineHeight: 1,
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <InputGroup.Append>
            <Button variant="outline-secondary" onClick={handleSearch} className="btn-sm">
              🔍
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </div>
  
        {/* Search Group End */}
  
        {/* Divider */}
      {/* Sort + Direction Group */}
      <div className="d-flex align-items-center gap-2">
        <Form.Control
          as="select"
          size="sm"
          value={selectedSort}
          onChange={(e) => onSortChange(e.target.value)}
          style={{ width: "auto", minWidth: 150 }}
        >
          {sortOptions.map((option) => (
            <option key={option} value={option}>
              Sort by: {option}
            </option>
          ))}
        </Form.Control>

        <Button variant="outline-secondary" onClick={onToggleDirection} className="btn-sm">
          {ascending ? "⬆️ Asc" : "⬇️ Desc"}
        </Button>
      </div>

      {/* Paging Group */}
      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
        <ButtonGroup>
          <Button
            className="btn-sm"
            variant="outline-secondary"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            ◀️
          </Button>
          <Button
            className="btn-sm"
            variant="outline-secondary"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            ▶️
          </Button>
        </ButtonGroup>

        <Form.Control
          as="select"
          size="sm"
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          style={{ width: "auto", minWidth: 100 }}
        >
          {itemsPerPageOptions.map((num) => (
            <option key={num} value={num}>
              {num} / page
            </option>
          ))}
        </Form.Control>

        <div className="text-nowrap small text-muted ml-2">
          Page {currentPage} of {Math.max(1, totalPages)} | Showing{" "}
          {Math.min(itemsPerPage, totalItems - (currentPage - 1) * itemsPerPage)} of {totalItems}
        </div>
      </div>
    </div>
  );
};

export default ListToolbar;
